import * as Sentry from "@sentry/react";
import {
  importMapDocument,
  type MapDocument,
} from "@west-marches/shared";
import type { RichTextDocument } from "@/components/editor/types";
import { normalizeNoteContent } from "@/components/notes/noteContent";
import { apiFetch } from "@/lib/apiClient";

import { getSupabaseBrowserClient } from "@/lib/supabase";

// Cache: starter campaigns rarely change, and the anonymous landing flow reads
// it during first-time local session setup. One in-flight promise + one
// resolved value.
let cachedStarter: MapDocument | null | undefined;
let inFlight: Promise<MapDocument | null> | null = null;
let cachedStarterNotebook: RichTextDocument | null | undefined;
let starterNotebookInFlight: Promise<RichTextDocument | null> | null = null;

interface StarterCampaignRow {
  document: unknown;
}

interface StarterNotebookResponse {
  note: {
    content: unknown;
  } | null;
}

// Fetches the starter campaign's stored document (a MapDocument JSON) using the
// public Supabase browser client. RLS gates this to the single row where
// `is_starter = true`, so anon and authenticated users both succeed when one
// exists. Returns null when no starter is set, when the row's document fails
// to import, or when the request errors — callers fall back to a blank
// rectangle in those cases.
export async function fetchStarterCampaignDocument(): Promise<MapDocument | null> {
  if (cachedStarter !== undefined) {
    return cachedStarter;
  }
  if (inFlight) {
    return inFlight;
  }

  inFlight = (async (): Promise<MapDocument | null> => {
    try {
      const supabase = getSupabaseBrowserClient();
      const { data, error } = await supabase
        .from("campaigns")
        .select("document")
        .eq("is_starter", true)
        .maybeSingle<StarterCampaignRow>();

      if (error) {
        // PGRST116 = no rows; treat the same as "no starter configured".
        if (error.code !== "PGRST116") {
          Sentry.captureException(error, {
            tags: { area: "starter-campaign", action: "fetch" },
          });
        }
        cachedStarter = null;
        return null;
      }

      if (!data?.document) {
        cachedStarter = null;
        return null;
      }

      // Run the document through the importer/normalizer so v1 starters are
      // upgraded to v2 (and any future schema bumps continue to work) before
      // we hand the document to a new campaign.
      const imported = importMapDocument(data.document);
      cachedStarter = {
        version: 2,
        savedAt: new Date().toISOString(),
        map: {
          bounds: imported.mapState.bounds,
          hexes: imported.mapState.hexes,
          decorations: imported.mapState.decorations,
          textLabels: imported.mapState.textLabels,
          partyPosition: imported.mapState.partyPosition,
        },
        editor: imported.editorState,
      };
      return cachedStarter;
    } catch (error) {
      Sentry.captureException(error, {
        tags: { area: "starter-campaign", action: "fetch" },
      });
      cachedStarter = null;
      return null;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

export async function fetchStarterCampaignNotebookContent(): Promise<RichTextDocument | null> {
  if (cachedStarterNotebook !== undefined) {
    return cachedStarterNotebook;
  }
  if (starterNotebookInFlight) {
    return starterNotebookInFlight;
  }

  starterNotebookInFlight = (async (): Promise<RichTextDocument | null> => {
    try {
      const response = await apiFetch("/api/campaigns/starter/notebook/gm", {
        method: "GET",
      });
      const payload = (await response.json().catch(() => ({}))) as StarterNotebookResponse & {
        error?: string;
      };

      if (!response.ok) {
        throw new Error(
          typeof payload.error === "string" && payload.error
            ? payload.error
            : "Failed to fetch starter notebook.",
        );
      }

      cachedStarterNotebook = payload.note?.content
        ? normalizeNoteContent(payload.note.content)
        : null;
      return cachedStarterNotebook;
    } catch (error) {
      Sentry.captureException(error, {
        tags: { area: "starter-campaign", action: "fetch-notebook" },
      });
      cachedStarterNotebook = null;
      return null;
    } finally {
      starterNotebookInFlight = null;
    }
  })();

  return starterNotebookInFlight;
}

// Test seam: lets unit tests reset the in-memory cache between cases.
export function resetStarterCampaignCacheForTests(): void {
  cachedStarter = undefined;
  inFlight = null;
  cachedStarterNotebook = undefined;
  starterNotebookInFlight = null;
}
